import {
  Box,
  Paper,
  Typography,
  IconButton,
  Slider,
  Stack,
  Avatar,
  Tooltip,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
// ⚡ Path-based imports (6× faster than barrel imports)
import PlayArrow from '@mui/icons-material/PlayArrow';
import Pause from '@mui/icons-material/Pause';
import SkipNext from '@mui/icons-material/SkipNext';
import SkipPrevious from '@mui/icons-material/SkipPrevious';
import VolumeUp from '@mui/icons-material/VolumeUp';
import VolumeOff from '@mui/icons-material/VolumeOff';
import DevicesIcon from '@mui/icons-material/Devices';
import ComputerIcon from '@mui/icons-material/Computer';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SpeakerIcon from '@mui/icons-material/Speaker';
import LaptopIcon from '@mui/icons-material/Laptop';
import CheckIcon from '@mui/icons-material/Check';
import { useState, useEffect, useRef } from 'react';
import { useLayout } from '@/contexts/LayoutContext';

interface SpotifyDevice {
  id: string;
  name: string;
  type: string;
  is_active: boolean;
  volume_percent: number;
}

interface PlaybackFooterProps {
  // Track info
  track?: {
    name: string;
    artists: Array<{ name: string }>;
    album: {
      name: string;
      images: Array<{ url: string }>;
    };
    duration_ms: number;
  };
  
  // Playback state
  isPlaying: boolean;
  position: number; // milliseconds
  isReady: boolean;
  volume: number; // 0-100
  
  // Playback controls
  onPlayPause: () => void;
  onSeek: (positionMs: number) => void; // Position in milliseconds
  onVolumeChange: (volume: number) => void; // Volume 0-100
  
  // Device controls
  devices?: SpotifyDevice[];
  activeDeviceId?: string | null;
  onDeviceChange?: (deviceId: string) => void;
  onRefreshDevices?: () => void;
  
  // Optional
  onNext?: () => void;
  onPrevious?: () => void;
}

export function PlaybackFooter({
  track,
  isPlaying,
  position,
  isReady,
  volume,
  onPlayPause,
  onSeek,
  onVolumeChange,
  devices = [],
  activeDeviceId,
  onDeviceChange,
  onRefreshDevices,
  onNext,
  onPrevious,
}: PlaybackFooterProps) {
  const [isSeeking, setIsSeeking] = useState(false);
  const [seekPosition, setSeekPosition] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [previousVolume, setPreviousVolume] = useState(volume);
  const [deviceMenuAnchor, setDeviceMenuAnchor] = useState<null | HTMLElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  const { setPlaybackFooterHeight } = useLayout();

  useEffect(() => {
    if (!track) {
      setPlaybackFooterHeight(0);
      return;
    }
    const el = paperRef.current;
    if (el) {
      setPlaybackFooterHeight(el.offsetHeight);
    }
    return () => setPlaybackFooterHeight(0);
  }, [track, setPlaybackFooterHeight]);

  const getDeviceIcon = (deviceType: string) => {
    const type = deviceType.toLowerCase();
    if (type.includes('computer') || type.includes('desktop')) return <ComputerIcon fontSize="small" />;
    if (type.includes('smartphone') || type.includes('mobile')) return <PhoneAndroidIcon fontSize="small" />;
    if (type.includes('speaker')) return <SpeakerIcon fontSize="small" />;
    return <LaptopIcon fontSize="small" />;
  };

  if (!track) {
    return null; // Don't show footer if no track
  }

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleSeekChange = (_event: Event, value: number | number[]) => {
    setIsSeeking(true);
    setSeekPosition(value as number);
  };

  const handleSeekCommit = (_event: Event | React.SyntheticEvent, value: number | number[]) => {
    const newPosition = value as number;
    onSeek(newPosition);
    // Keep seeking true for a brief moment to prevent position flicker
    setTimeout(() => setIsSeeking(false), 100);
  };

  const handleSeekStart = () => {
    setIsSeeking(true);
  };

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const currentPosition = isSeeking ? seekPosition : position;
  const progress = (currentPosition / track.duration_ms) * 100;

  const seekSlider = (
    <Slider
      value={currentPosition}
      min={0}
      max={track.duration_ms}
      onChange={handleSeekChange}
      onChangeCommitted={handleSeekCommit}
      onMouseDown={handleSeekStart}
      onTouchStart={handleSeekStart}
      disabled={!isReady}
      sx={{
        flex: 1,
        '& .MuiSlider-thumb': {
          width: isMobile ? 20 : 12,
          height: isMobile ? 20 : 12,
          transition: 'width 0.2s, height 0.2s',
          '&:hover, &.Mui-active': {
            width: isMobile ? 24 : 16,
            height: isMobile ? 24 : 16,
          },
        },
        '& .MuiSlider-rail': {
          opacity: 0.3,
          height: isMobile ? 6 : 4,
        },
        '& .MuiSlider-track': {
          height: isMobile ? 6 : 4,
        },
      }}
    />
  );

  const deviceMenu = devices.length > 0 && onDeviceChange && (
    <>
      <Tooltip title="Playback device" disableTouchListener>
        <IconButton
          size="small"
          onClick={(e) => setDeviceMenuAnchor(e.currentTarget)}
          aria-label="Playback device"
          sx={{
            minWidth: 44,
            minHeight: 44,
            color: devices.some(d => d.is_active) ? 'primary.main' : 'text.secondary',
          }}
        >
          <DevicesIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={deviceMenuAnchor}
        open={Boolean(deviceMenuAnchor)}
        onClose={() => setDeviceMenuAnchor(null)}
        disableScrollLock={true}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ style: { maxHeight: 400 } }}
      >
        {[
          ...devices.map((device) => (
            <MenuItem
              key={device.id}
              onClick={() => {
                onDeviceChange?.(device.id);
                setDeviceMenuAnchor(null);
              }}
              selected={device.id === activeDeviceId}
            >
              <ListItemIcon>
                {device.id === activeDeviceId ? (
                  <CheckIcon fontSize="small" color="primary" />
                ) : (
                  getDeviceIcon(device.type)
                )}
              </ListItemIcon>
              <ListItemText
                primary={device.name}
                secondary={device.type}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </MenuItem>
          )),
          ...(onRefreshDevices ? [
            <Divider key="divider" />,
            <MenuItem key="refresh" onClick={() => {
              onRefreshDevices();
              setDeviceMenuAnchor(null);
            }}>
              <ListItemIcon>
                <DevicesIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Refresh devices" />
            </MenuItem>
          ] : [])
        ]}
      </Menu>
    </>
  );

  if (isMobile) {
    return (
      <Paper
        ref={paperRef}
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          borderTop: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'blur(10px)',
          bgcolor: (t) =>
            t.palette.mode === 'dark'
              ? 'rgba(18, 18, 18, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
        }}
      >
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 3,
            bgcolor: 'action.hover',
            '& .MuiLinearProgress-bar': {
              bgcolor: 'primary.main',
              transition: isSeeking ? 'none' : 'transform 0.1s linear',
            },
          }}
        />

        <Box sx={{ px: 2, py: 1 }}>
          {/* Row 1: Track info + controls */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={track.album.images[0]?.url}
              alt={track.album.name}
              variant="rounded"
              sx={{ width: 44, height: 44 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight="medium" noWrap>
                {track.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {track.artists.map((a) => a.name).join(', ')}
              </Typography>
            </Box>

            {onPrevious && (
              <IconButton onClick={onPrevious} disabled={!isReady} aria-label="Previous" sx={{ minWidth: 44, minHeight: 44 }}>
                <SkipPrevious />
              </IconButton>
            )}
            <IconButton
              onClick={onPlayPause}
              disabled={!isReady}
              aria-label={isPlaying ? 'Pause' : 'Play'}
              sx={{
                minWidth: 48,
                minHeight: 48,
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': { bgcolor: 'action.disabledBackground' },
              }}
            >
              {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>
            {onNext && (
              <IconButton onClick={onNext} disabled={!isReady} aria-label="Next" sx={{ minWidth: 44, minHeight: 44 }}>
                <SkipNext />
              </IconButton>
            )}
          </Stack>

          {/* Row 2: Seek bar */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32, textAlign: 'right', fontSize: '0.65rem' }}>
              {formatTime(currentPosition)}
            </Typography>
            {seekSlider}
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32, fontSize: '0.65rem' }}>
              {formatTime(track.duration_ms)}
            </Typography>
            {deviceMenu}
          </Stack>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      ref={paperRef}
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        borderTop: '1px solid',
        borderColor: 'divider',
        backdropFilter: 'blur(10px)',
        bgcolor: (t) =>
          t.palette.mode === 'dark'
            ? 'rgba(18, 18, 18, 0.95)'
            : 'rgba(255, 255, 255, 0.95)',
      }}
    >
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 3,
          bgcolor: 'action.hover',
          '& .MuiLinearProgress-bar': {
            bgcolor: 'primary.main',
            transition: isSeeking ? 'none' : 'transform 0.1s linear',
          },
        }}
      />

      <Box sx={{ px: 3, py: 1.5 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Track Info (Left) */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: '0 0 300px' }}>
            <Avatar
              src={track.album.images[0]?.url}
              alt={track.album.name}
              variant="rounded"
              sx={{ width: 56, height: 56 }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body1" fontWeight="medium" noWrap sx={{ color: 'text.primary' }}>
                {track.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {track.artists.map((a) => a.name).join(', ')}
              </Typography>
            </Box>
          </Stack>

          {/* Playback Controls (Center) */}
          <Stack spacing={1} alignItems="center" sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              {onPrevious && (
                <Tooltip title="Previous" disableTouchListener>
                  <IconButton size="small" onClick={onPrevious} disabled={!isReady} aria-label="Previous">
                    <SkipPrevious />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={isPlaying ? 'Pause' : 'Play'} disableTouchListener>
                <span>
                  <IconButton
                    size="large"
                    onClick={onPlayPause}
                    disabled={!isReady}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' },
                      '&:disabled': { bgcolor: 'action.disabledBackground' },
                    }}
                  >
                    {isPlaying ? <Pause /> : <PlayArrow />}
                  </IconButton>
                </span>
              </Tooltip>
              {onNext && (
                <Tooltip title="Next" disableTouchListener>
                  <IconButton size="small" onClick={onNext} disabled={!isReady} aria-label="Next">
                    <SkipNext />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', maxWidth: 600 }}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40, textAlign: 'right' }}>
                {formatTime(currentPosition)}
              </Typography>
              {seekSlider}
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>
                {formatTime(track.duration_ms)}
              </Typography>
            </Stack>
          </Stack>

          {/* Device & Volume Control (Right) */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: '0 0 250px', justifyContent: 'flex-end' }}>
            {deviceMenu}
            <Tooltip title={isMuted ? 'Unmute' : 'Mute'} disableTouchListener>
              <IconButton
                size="small"
                aria-label={isMuted ? 'Unmute' : 'Mute'}
                onClick={() => {
                  if (isMuted) {
                    setIsMuted(false);
                    onVolumeChange(previousVolume || 50);
                  } else {
                    setPreviousVolume(volume);
                    setIsMuted(true);
                    onVolumeChange(0);
                  }
                }}
                disabled={!isReady}
              >
                {isMuted || volume === 0 ? <VolumeOff /> : <VolumeUp />}
              </IconButton>
            </Tooltip>
            <Slider
              value={isMuted ? 0 : volume}
              min={0}
              max={100}
              onChange={(_e, value) => {
                const newVolume = value as number;
                onVolumeChange(newVolume);
                if (newVolume > 0 && isMuted) {
                  setIsMuted(false);
                }
              }}
              disabled={!isReady}
              sx={{
                width: 100,
                '& .MuiSlider-thumb': { width: 10, height: 10 },
              }}
            />
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
}
